import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {IncidentResponse, ReferenceBookResponse} from '@npck/af-frontend-api';
import {Spinner} from '@npck/libs/commons';

@Component({
    selector: 'af-amount-information-step',
    templateUrl: './amount-information-step.component.html',
    styleUrls: ['./amount-information-step.component.scss'],
    standalone: false
})
export class AmountInformationStepComponent implements OnInit {
    @Input() form: FormGroup<IncidentAmountForm>;
    @Input() incident: IncidentResponse;
    @Output() nextStep = new EventEmitter<void>;
    @Output() cancel = new EventEmitter<void>;
    Spinner = Spinner;
    Validators = Validators;
    currencies: ReferenceBookResponse[];

    fields = ['lossCredit', 'lossOwn', 'saveCredit', 'saveOwn'];

    ngOnInit(): void {
        this.form.addControl('lossCreditAmount', new FormControl(null));
        this.form.addControl('lossCreditAmountCurrency', new FormControl(null));
        this.form.addControl('lossOwnAmount', new FormControl(null));
        this.form.addControl('lossOwnAmountCurrency', new FormControl(null));
        this.form.addControl('saveCreditAmount', new FormControl(null));
        this.form.addControl('saveCreditAmountCurrency', new FormControl(null));
        this.form.addControl('saveOwnAmount', new FormControl(null));
        this.form.addControl('saveOwnAmountCurrency', new FormControl(null));
        this.form.addControl('lossTotalAmount', new FormControl({value: null, disabled: true}));

        this.subscribeToFormInitialization();
        this.form.valueChanges.subscribe(value => {
            const sum = [value.lossCreditAmount, value.lossOwnAmount].map(lossValue => Number(lossValue)).reduce((a, b) => a + b, 0);
            this.form.controls.lossTotalAmount.setValue(sum, {emitEvent: false});
        });
    }

    getCurrencyValue(field: string): string {
        if(this.incident) {
            switch (field) {
                case 'lossCredit':
                    return this.incident.currency_amount_loss_credit;
                case 'lossOwn':
                    return this.incident.currency_amount_loss_own;
                case 'saveCredit':
                    return this.incident.currency_amount_save_credit;
                case 'saveOwn':
                    return this.incident.currency_amount_save_own;
            }
        }
        return null;
    }

    skipStep(): void {
        Object.keys(this.form.controls)
            .forEach((controlName: string) => {
                this.form.get(controlName).reset();
                this.form.get(controlName).setValidators(null);
                this.form.get(controlName).updateValueAndValidity();
            });
        this.nextStep.emit();
    }

    getControl(control: string): FormControl {
        return this.form.get(control) as FormControl;
    }

    submit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        this.nextStep.emit();
    }

    private subscribeToFormInitialization(): void {
        if (this.incident && Number(this.incident.amount_loss_credit)) {
            this.form.controls.lossCreditAmount.setValue(this.incident.amount_loss_credit);
            this.form.controls.lossOwnAmount.setValue(this.incident.amount_loss_own);
            this.form.controls.saveCreditAmount.setValue(this.incident.amount_save_credit);
            this.form.controls.saveOwnAmount.setValue(this.incident.amount_save_own);
        } else if (this.incident) {
            Object.keys(this.form.controls)
                .forEach((controlName: string) => {
                    this.form.get(controlName).reset();
                    this.form.get(controlName).setValidators(null);
                    this.form.get(controlName).updateValueAndValidity();
                });
        }
    }
}

export interface IncidentAmountForm {
    lossCreditAmount?: FormControl<string>;
    lossCreditAmountCurrency?: FormControl<string>;
    lossOwnAmount?: FormControl<string>;
    lossOwnAmountCurrency?: FormControl<string>;
    saveCreditAmount?: FormControl<string>;
    saveCreditAmountCurrency?: FormControl<string>;
    saveOwnAmount?: FormControl<string>;
    saveOwnAmountCurrency?: FormControl<string>;
    lossTotalAmount?: FormControl<number>;
}
